import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import LinkIcon from 'ext-ui/svg/icon/LinkIcon'
import React, { KeyboardEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { VirtualAccountView } from 'src/api/query/creditcards'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import EnableVirtualCardsModal from 'src/component-modal/EnableVirtualCardsModal/EnableVirtualCardsModal'
import { useTranslate } from 'src/localization/Trns'

import style from './OrgVirtualCardsEnablePanel.style'

type OrgVirtualCardsEnablePanelProps = {
  className?: string
  virtualAccount: VirtualAccountView
}

export default function OrgVirtualCardsEnablePanel(
  props: OrgVirtualCardsEnablePanelProps,
) {
  const { className = '', virtualAccount } = props

  const [showEnableModal, setShowEnableModal] = useState<boolean>(false)

  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const isActive = virtualAccount.status === 'ACTIVE'
  // TODO: PENDING status - treat same as NOT_STARTED for now
  const showAlertPanel =
    virtualAccount.status === 'NOT_STARTED' ||
    virtualAccount.status === 'PENDING'
  const showConnectCta = !isActive

  const handleConnectClick = () => {
    analytics.track('connect virtual cards org settings')
    setShowEnableModal(true)
  }

  return (
    <div
      className={cn('OrgVirtualCardsEnablePanel', className, {
        'has-top-alert-panel': showAlertPanel,
      })}
      css={style}
    >
      {showAlertPanel && (
        <AlertPanel variant="warning-wcag">
          <div className="flex-row gap-4 align-top">
            <AlertTriangleYellowIcon />
            <div className="flex-column gap-2">
              <p className="font-bold">
                {translate('Disconnected virtual card integration')}
              </p>
              <p className="font-regular">
                {translate(
                  'Connect to enable virtual card capabilities for this organization.',
                )}
              </p>
            </div>
          </div>
        </AlertPanel>
      )}
      <ContentCard>
        <div className="flex-column gap-5">
          <div className="flex-row justify-start gap-2">
            {isActive && <CheckmarkCircleIcon />}
            <p className="font-h3">
              {translate(
                isActive
                  ? 'Virtual Card Capabilities Enabled'
                  : 'Enable Virtual Card Capabilities',
              )}
            </p>
          </div>
          <p className="font-regular">
            {translate(
              'Create virtual cards to control spend, reduce risk, and simplify payments.',
            )}
          </p>
        </div>
      </ContentCard>
      {showConnectCta && (
        <div className="OrgVirtualCardsEnablePanel-bottom">
          <div
            className="linktext-content"
            role="button"
            aria-label={translate(
              'Enable virtual card capabilities for this organization',
            )}
            onClick={handleConnectClick}
            tabIndex={0}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleConnectClick()
              }
            }}
          >
            <div className="flex-row align-center gap-2">
              <p className="font-linktext connect-text">
                {translate('Connect')}
              </p>
              <LinkIcon />
            </div>
          </div>
        </div>
      )}
      <EnableVirtualCardsModal
        isOpen={showEnableModal}
        showCloseButton
        onClose={() => setShowEnableModal(false)}
        creditCardId={virtualAccount.creditCardId}
        onEnableComplete={() => {
          setShowEnableModal(false)
          queryClient.invalidateQueries({
            queryKey: ['creditcardsv2', 'virtualaccounts'],
          })
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <p>
                  {translate(
                    'Success! Virtual card capabilities are now enabled.',
                  )}
                </p>
              ),
            }),
          )
        }}
        onEnableError={() => {
          setShowEnableModal(false)
        }}
      />
    </div>
  )
}
